import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useSelector } from 'react-redux';
import http from '../api';
import { toast } from 'react-toastify';

const TopProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(5); // Top 5 o 10
  const { user } = useSelector((state) => state.auth);
  const { tenant } = useSelector((state) => state.tenant);
  
  // Obtener tenantId del usuario o del tenant
  const tenantId = user?.tenantId?._id || user?.tenantId || tenant?._id || tenant?.id;

  useEffect(() => {
    if (tenantId) {
      fetchTopProducts();
    } else {
      // If no tenantId yet, show empty data to avoid unnecessary requests
      setProducts(generateMockData(limit).map(p => ({ name: p.name, cantidad: 0, ingresos: 0 })));
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [limit, tenantId]);

  const fetchTopProducts = async () => {
    try {
      setLoading(true);
      if (!tenantId) return;
      const response = await http.get('/reports/top-products', {
        params: {
          tenantId: tenantId,
          limit: limit
        }
      });

      // Formatear datos para el gráfico
      const formatted = response.data.map(item => ({
        name: item.name?.substring(0, 20) || 'Sin nombre',
        cantidad: item.totalQuantity,
        ingresos: item.totalRevenue
      }));

      setProducts(formatted);
    } catch (error) {
      console.error('Error al cargar productos top:', error);
      // Reuse same toast id so we don't flood the UI with multiple similar messages
      toast.error('Error al cargar productos más vendidos', { toastId: 'reports-error' });
      // Datos de ejemplo en caso de error
      setProducts(generateMockData(limit));
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = (count) => {
    const mockProducts = [
      'Café Premium',
      'Galletas Chocolate',
      'Refresco Cola',
      'Pan Integral',
      'Leche Entera',
      'Arroz Blanco',
      'Frijoles Negros',
      'Aceite Vegetal',
      'Azúcar Morena',
      'Huevos Frescos'
    ];

    return mockProducts.slice(0, count).map(name => ({
      name,
      cantidad: Math.floor(Math.random() * 100) + 20,
      ingresos: Math.floor(Math.random() * 50000) + 10000
    }));
  };

  const formatCurrency = (value) => {
    try {
      const n = Number(value || 0);
      return `$${n.toLocaleString('es-ES', { minimumFractionDigits: 0 })}`;
    } catch (e) {
      return `$0`;
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#8DD1E1', '#D084D0', '#A4DE6C'];

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>🏆 Productos Más Vendidos</h3>
        <select 
          value={limit} 
          onChange={(e) => setLimit(Number(e.target.value))}
          style={styles.select}
        >
          <option value={5}>Top 5</option>
          <option value={10}>Top 10</option>
        </select>
      </div>

      {/* Gráfico de barras */}
      <ResponsiveContainer width="100%" height={250}>
        <BarChart 
          data={products} 
          layout="vertical"
          margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis dataKey="name" type="category" width={90} />
          <Tooltip 
            formatter={(value, name) => [
              name === 'ingresos' ? formatCurrency(value) : value,
              name === 'cantidad' ? 'Unidades' : 'Ingresos'
            ]}
          />
          <Bar dataKey="cantidad" fill="#8884d8" name="Cantidad">
            {products.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Tabla detallada */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>#</th>
              <th style={styles.th}>Producto</th>
              <th style={styles.th}>Unidades</th>
              <th style={styles.th}>Ingresos</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, index) => (
              <tr key={index} style={styles.tr}>
                <td style={styles.td}>
                  <span style={{
                    ...styles.badge,
                    backgroundColor: COLORS[index % COLORS.length]
                  }}>
                    {index + 1}
                  </span>
                </td>
                <td style={styles.td}>{product.name}</td>
                <td style={{...styles.td, textAlign: 'center'}}>{product.cantidad}</td>
                <td style={{...styles.td, textAlign: 'right'}}>
                  {formatCurrency(product.ingresos)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '20px'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px'
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    color: '#333'
  },
  select: {
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '14px'
  },
  tableContainer: {
    marginTop: '20px',
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    padding: '12px',
    textAlign: 'left',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #dee2e6',
    fontWeight: '600',
    color: '#495057'
  },
  tr: {
    borderBottom: '1px solid #dee2e6'
  },
  td: {
    padding: '12px'
  },
  badge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '12px',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '12px',
    minWidth: '24px',
    textAlign: 'center'
  }
};

export default TopProducts;
